import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Phone, Mail, MapPin, Facebook, Anchor, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { WaveDivider } from "@/components/WaveDivider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactSchema = z.object({
  first_name: z.string().min(2, "First name must be at least 2 characters"),
  last_name: z.string().min(2, "Last name must be at least 2 characters"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

const submitContact = async (data) => axios.post(`${API}/contact`, data);

const HeroSection = () => (
  <section
    className="relative pt-32 pb-20 md:pt-40 md:pb-28"
    style={{
      background: "linear-gradient(135deg, #E91E8C 0%, #C8147A 50%, #00B4D8 100%)",
    }}
  >
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-24 left-10 opacity-10">
        <Anchor className="w-24 h-24 text-white" />
      </div>
    </div>

    <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <span className="font-accent text-3xl md:text-4xl text-white/90">
        Get In Touch
      </span>
      <h1 className="font-heading font-bold text-5xl md:text-7xl text-white mt-2 mb-6 text-shadow">
        Contact Us
      </h1>
      <p className="font-body text-xl text-white/90 max-w-2xl mx-auto">
        Have questions? Want to learn more? We'd love to hear from you!
      </p>
    </div>

    <div className="absolute bottom-0 left-0 right-0">
      <WaveDivider color="#FAF4E1" />
    </div>
  </section>
);

const ContactCard = ({ href, icon: Icon, iconColor, label, value, subtext }) => {
  const content = (
    <>
      <div className={`w-14 h-14 ${iconColor} rounded-full flex items-center justify-center group-hover:opacity-80 transition-colors duration-300`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="font-body text-sm text-gray-500">{label}</p>
        <p className="font-heading font-bold text-lg text-seaside-navy">{value}</p>
        {subtext && <p className="font-body text-sm text-gray-500">{subtext}</p>}
      </div>
    </>
  );

  const baseClasses = "flex items-center gap-4 p-4 bg-white rounded-2xl shadow-md";

  if (href) {
    return (
      <a
        href={href}
        data-testid={`contact-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
        className={`${baseClasses} hover:shadow-lg transition-shadow duration-300 group`}
      >
        {content}
      </a>
    );
  }

  return <div className={baseClasses}>{content}</div>;
};

const ContactInfo = () => (
  <div className="space-y-8">
    <div>
      <h2 className="font-heading font-bold text-3xl md:text-4xl text-seaside-navy mb-4">
        Let's Connect
      </h2>
      <p className="font-body text-lg text-gray-600">
        Whether you have a question about our menu, want to book us for
        an event, or just want to say hello – we're here for you!
      </p>
    </div>

    <div className="space-y-6">
      <ContactCard
        href="tel:727-833-9500"
        icon={Phone}
        iconColor="bg-seaside-magenta/10 text-seaside-magenta"
        label="Call Us"
        value="727-833-9500"
      />
      <ContactCard
        href="mailto:seasidebitespasco@gmail.com"
        icon={Mail}
        iconColor="bg-seaside-teal/10 text-seaside-teal"
        label="Email Us"
        value="seasidebitespasco@gmail.com"
      />
      <ContactCard
        icon={MapPin}
        iconColor="bg-seaside-gold/20 text-seaside-gold"
        label="Location"
        value="Pasco County, Florida"
        subtext="Serving the surrounding islands on the water"
      />
    </div>

    <div className="pt-4">
      <a
        href="https://www.facebook.com/share/1DrweEa7SG/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="contact-facebook-link"
        className="inline-flex items-center gap-3 px-6 py-4 bg-[#1877F2] text-white font-heading font-bold rounded-full hover:bg-[#166FE5] transition-colors duration-300"
      >
        <Facebook className="w-6 h-6" />
        Follow Us On Facebook
      </a>
    </div>
  </div>
);

const SuccessMessage = ({ onReset }) => (
  <div
    data-testid="contact-success"
    className="bg-white rounded-3xl p-8 md:p-12 text-center shadow-xl"
  >
    <div className="w-20 h-20 bg-seaside-teal/20 rounded-full flex items-center justify-center mx-auto mb-6">
      <Send className="w-10 h-10 text-seaside-teal" />
    </div>
    <h2 className="font-heading font-bold text-3xl text-seaside-navy mb-4">
      Message Sent!
    </h2>
    <p className="font-body text-lg text-gray-600 mb-8">
      Thank you for reaching out! We'll get back to you as soon as possible.
    </p>
    <Button
      data-testid="send-another-btn"
      onClick={onReset}
      className="btn-primary px-8 py-4 font-heading font-bold rounded-full"
    >
      Send Another Message
    </Button>
  </div>
);

const FormField = ({ label, id, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="font-body font-semibold text-seaside-navy">
      {label}
    </Label>
    {children}
    {error && <p className="text-red-500 text-sm">{error}</p>}
  </div>
);

const ContactForm = ({ form, onSubmit, isSubmitting }) => {
  const { register, handleSubmit, formState: { errors } } = form;

  return (
    <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
      <h2 className="font-heading font-bold text-2xl text-seaside-navy mb-6">
        Send Us a Message
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <FormField label="First Name *" id="first_name" error={errors.first_name?.message}>
          <Input
            id="first_name"
            data-testid="contact-first-name"
            placeholder="Enter your first name"
            className="input-seaside py-3"
            {...register("first_name")}
          />
        </FormField>

        <FormField label="Last Name *" id="last_name" error={errors.last_name?.message}>
          <Input
            id="last_name"
            data-testid="contact-last-name"
            placeholder="Enter your last name"
            className="input-seaside py-3"
            {...register("last_name")}
          />
        </FormField>

        <FormField label="Phone Number *" id="phone" error={errors.phone?.message}>
          <Input
            id="phone"
            type="tel"
            data-testid="contact-phone"
            placeholder="(555) 123-4567"
            className="input-seaside py-3"
            {...register("phone")}
          />
        </FormField>

        <FormField label="Message *" id="message" error={errors.message?.message}>
          <Textarea
            id="message"
            data-testid="contact-message"
            placeholder="How can we help you?"
            className="input-seaside min-h-32"
            {...register("message")}
          />
        </FormField>

        <Button
          type="submit"
          data-testid="contact-submit-btn"
          disabled={isSubmitting}
          className="w-full btn-primary py-4 text-lg font-heading font-bold rounded-full"
        >
          {isSubmitting ? "Sending..." : "Send Message"}
        </Button>
      </form>
    </div>
  );
};

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: { first_name: "", last_name: "", phone: "", message: "" },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await submitContact(data);
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you soon.");
      form.reset();
    } catch {
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="overflow-hidden">
      <HeroSection />
      <section className="bg-seaside-sand py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <ContactInfo />
            <div>
              {submitted ? (
                <SuccessMessage onReset={() => setSubmitted(false)} />
              ) : (
                <ContactForm form={form} onSubmit={onSubmit} isSubmitting={isSubmitting} />
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
